import * as React from 'react';
import useField from './useField';
import configureOverlayForm from '../components/ChallengeUsPage/api';
import isEmail from 'validator/lib/isEmail';

interface Values {
  name: string;
  email: string;
  description: string;
  source: string;
}

interface Setters {
  setName: (value: string) => void;
  setEmail: (value: string) => void;
  setDescription: (value: string) => void;
  setSource: (value: string) => void;
}

interface Errors {
  nameError: string;
  emailError: string;
  descriptionError: string;
}

export enum ResponseState {
  Fail,
  Success,
  None,
}

const useContactFrom = ({
  onSuccess,
  setRequestResponse,
}: {
  setRequestResponse?: (state: ResponseState) => void;
  onSuccess?: () => void;
}): [Values, Errors, Setters, () => void, ResponseState, boolean, boolean] => {
  const [responseState, setResponse] = React.useState<ResponseState>(
    ResponseState.None
  );
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [name, nameError, setName, setNameError, resetName] = useField('');
  const [email, emailError, setEmail, setEmailError, resetEmail] = useField('');
  const [
    description,
    descriptionError,
    setDescription,
    setDescriptionError,
    resetDescription,
  ] = useField('');

  React.useEffect(() => {
    if (nameError && name.trim()) {
      setNameError('');
    }
  }, [name, nameError]);

  React.useEffect(() => {
    if (emailError && isEmail(email)) {
      setEmailError('');
    }
  }, [email, emailError]);

  React.useEffect(() => {
    if (descriptionError && description.trim()) {
      setDescriptionError('');
    }
  }, [description, descriptionError]);

  const [source, setSource] = React.useState('');

  const clearFields = React.useCallback(() => {
    resetName();
    resetEmail();
    resetDescription();
    setSource('');
  }, [resetDescription, resetEmail, resetName]);

  const onSetResponse = React.useCallback(
    (json?: { field: string; message: string }) => {
      switch (json?.field) {
        case 'name': {
          setResponse(ResponseState.Fail);
          setRequestResponse?.(ResponseState.Fail);
          setNameError(json?.message ?? '');
          break;
        }

        case 'email': {
          setResponse(ResponseState.Fail);
          setRequestResponse?.(ResponseState.Fail);
          setEmailError(json.message);
          break;
        }

        case 'description': {
          setResponse(ResponseState.Fail);
          setRequestResponse?.(ResponseState.Fail);
          setDescriptionError(json.message);
          break;
        }

        default:
          onSuccess?.();
          clearFields();
          setResponse(ResponseState.Success);
          setRequestResponse?.(ResponseState.Success);
      }

      setIsProcessing(false);
    },
    [
      onSuccess,
      clearFields,
      setRequestResponse,
      setNameError,
      setEmailError,
      setDescriptionError,
    ]
  );

  const onSubmit = React.useCallback(() => {
    let hasError = false;

    if (!name.trim()) {
      setNameError('Name is required');
      hasError = true;
    }

    if (!isEmail(email)) {
      setEmailError('Email is invalid');
      hasError = true;
    }

    if (!description.trim()) {
      setDescriptionError('Description is required');
      hasError = true;
    }

    if (hasError) {
      setResponse(ResponseState.Fail);
      setRequestResponse?.(ResponseState.Fail);

      return;
    }

    setIsProcessing(true);

    // @ts-ignore
    const woopraId = window?.woopra?.cookie ?? 'no-id';

    configureOverlayForm(
      {
        name,
        email,
        description,
        source,
        woopraId,
      },
      onSetResponse
    );
  }, [
    name,
    email,
    description,
    source,
    onSetResponse,
    setNameError,
    setEmailError,
    setDescriptionError,
    setRequestResponse,
  ]);

  const isSuccess = responseState === ResponseState.Success;

  return [
    { name, email, description, source },
    { nameError, emailError, descriptionError },
    { setName, setEmail, setDescription, setSource },
    onSubmit,
    responseState,
    isProcessing,
    isSuccess,
  ];
};

export default useContactFrom;
